import { makeAutoObservable } from 'mobx';
import { OptionCategoryModel, OptionCategory } from '../../../model/professional/option-category-model';
import toastStore from '../../../store/toast-store';
import categoryStore from '../../../store/category-store';

class OptionCategoryViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  private optionCategoryModel: OptionCategoryModel;
  
  setLoading(value: boolean): void {
    this.isLoading = value;
  }

  setError(value: string | null): void {
    this.error = value;
  }

  private setLoadingStates(isLoading: boolean): void {
    this.setLoading(isLoading);
    categoryStore.setLoading(isLoading);
    if (!isLoading) return; 
    this.setError(null);
    categoryStore.setError(null);
  }
  
  private handleError(message: string): void {
    console.error(message);
    this.setError(message);
    categoryStore.setError(message);
  }
  
  constructor() {
    makeAutoObservable(this);
    this.optionCategoryModel = OptionCategoryModel.getInstance();
  }

  async getCategories(serviceId: string): Promise<OptionCategory[]> {
    this.setLoadingStates(true);
    categoryStore.setSelectedServiceId(serviceId);
    
    try {
      const response = await this.optionCategoryModel.getCategories(serviceId);
      
      if (!response?.success || !Array.isArray(response.data)) {
        throw new Error(response?.message);
      }
  
      categoryStore.setCategories(response.data);
      return response.data;
    } catch (error: any) {
      this.handleError(error.message || `Impossible de récupérer les catégories pour le service ${serviceId}`);
      return [];
    } finally {
      this.setLoadingStates(false);
    }
  }

  async getCategoryById(categoryId: string): Promise<OptionCategory> {
    this.setLoadingStates(true);
    
    try {
      const existingCategory = categoryStore.getCategoryById(categoryId);
      if (existingCategory) {
        this.setLoadingStates(false);
        return existingCategory;
      }
  
      const response = await this.optionCategoryModel.getCategoryById(categoryId);
      
      if (!response?.success || !response.data) {
        throw new Error(response?.message);
      }
  
      categoryStore.addCategory(response.data);
      return response.data;
    } catch (error: any) {
      this.handleError(error.message || `Impossible de récupérer la catégorie ${categoryId}`);
      throw error;
    } finally {
      this.setLoadingStates(false);
    }
  }

  async deleteCategory(categoryId: string): Promise<boolean> {
    this.setLoadingStates(true);
    
    try {
      const response = await this.optionCategoryModel.deleteCategory(categoryId);
      
      if (!response?.success) {
        throw new Error(response?.message);
      }
  
      categoryStore.removeCategoryFromStore(categoryId);
      toastStore.success(response.message);
      return true;
    } catch (error: any) {
      this.handleError(error.message || `Impossible de supprimer la catégorie ${categoryId}`);
      throw error;
    } finally {
      this.setLoadingStates(false);
    }
  }
}

export const optionCategoryViewModel = new OptionCategoryViewModel();
